import { Popover, Tooltip } from "antd";
import { RefreshCcw } from "lucide-react";

const MonitorStatusBadge = ({ monitorStatus, fetchMonitorStatus }) => {
  const overlayContent = (
    <div className="w-[280px] 2xl:w-[340px] text-white">
      <div className="flex gap-3 items-center mb-3">
        <div className="bg-[#7D7D7D26] rounded-full p-[.6rem] flex justify-center items-center">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 7.50231L10 10.5023L14 12.5023L11 15.5023M20 12.0023C20 16.9108 14.646 20.4807 12.698 21.6172C12.4766 21.7463 12.3659 21.8109 12.2097 21.8444C12.0884 21.8704 11.9116 21.8704 11.7903 21.8444C11.6341 21.8109 11.5234 21.7463 11.302 21.6172C9.35396 20.4807 4 16.9108 4 12.0023V7.21991C4 6.4204 4 6.02065 4.13076 5.67702C4.24627 5.37345 4.43398 5.10259 4.67766 4.88784C4.9535 4.64475 5.3278 4.50439 6.0764 4.22366L11.4382 2.21299C11.6461 2.13503 11.75 2.09604 11.857 2.08059C11.9518 2.06689 12.0482 2.06689 12.143 2.08059C12.25 2.09604 12.3539 2.13503 12.5618 2.21299L17.9236 4.22366C18.6722 4.50439 19.0465 4.64475 19.3223 4.88784C19.566 5.10259 19.7537 5.37345 19.8692 5.67702C20 6.02065 20 6.4204 20 7.21991V12.0023Z"
              stroke="#F97066"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div className="text-[1.05rem] font-semibold text-[#FCFCFD]">
          System monitoring tool
        </div>
      </div>

      {/* Displays */}
      <div className="mb-5">
        <div className="text-[#98A2B3] text-[.94rem] font-semibold mb-2">
          Connected Displays{" "}
          <span className="text-[#53A0F8]">
            ({monitorStatus.display_count})
          </span>
        </div>

        <div className="border-b border-gray-600 mb-2"></div>

        <div className="space-y-1 text-sm">
          {monitorStatus.displays.map((display, i) => (
            <div key={i}>
              {i + 1}. {display.name} ({display.resolution})
            </div>
          ))}
        </div>
        {monitorStatus?.display_count > 1 && (
          <p className="text-red-500 text-[.82rem] font-medium leading-[1.1rem] mt-2">
            Note: More than one display is connected. Disconnect extra monitors
            to maintain interview integrity.
          </p>
        )}
      </div>

      {/* Apps */}
      <div className="">
        <div className="text-[#98A2B3] text-[.94rem] font-semibold mb-2">
          Applications{" "}
          <span className="text-[#53A0F8]">({monitorStatus.app_count})</span>
        </div>

        <div className="border-b border-gray-600 mb-2"></div>

        <div className="space-y-2 max-h-[160px] overflow-y-auto scroll pr-1">
          {monitorStatus?.apps?.map((app, i) => {
            const isMatched = monitorStatus?.suspicious_apps?.some(
              (suspicious) => suspicious.pid === app.pid,
            );

            return (
              <div
                key={app.pid || i}
                className={`text-sm px-2 py-1 ${
                  isMatched
                    ? "rounded-lg border-2 border-red-500 text-red-500"
                    : ""
                }`}
              >
                <div>
                  {i + 1}. {app.name}
                </div>

                <div
                  className={`text-xs truncate pl-4 ${
                    isMatched ? "text-red-400" : "text-gray-400"
                  }`}
                >
                  {app.title}
                </div>
              </div>
            );
          })}
        </div>
        {monitorStatus?.suspicious_count > 0 && (
          <div className="text-red-500 text-[.82rem] font-medium leading-[1.1rem] mt-2">
            Note: Applications marked in red are violating the interview
            guidelines. Please close or remove them to maintain interview
            integrity.
          </div>
        )}
      </div>
    </div>
  );

  const isWarning =
    monitorStatus?.suspicious_count > 0 || monitorStatus?.display_count > 1;

  return (
    <div className="flex gap-y-2 gap-x-[.8rem] items-center">
      <span>
        <Popover
          overlayInnerStyle={{
            color: "#D0D5DD",
            padding: "8px 12px",
            width: "280px",
            border: "1px solid lightgray",
            backgroundColor: "black",
          }}
          content={
            <div className="text-center text-medium text-[#D0D5DD]">
              Monitor status updates periodically. Click here to manually
              refresh and see the latest status.
            </div>
          }
        >
          <RefreshCcw
            className="cursor-pointer"
            color="#32D583"
            size={20}
            onClick={() => fetchMonitorStatus(false)}
          />
        </Popover>
      </span>
      <Popover
        content={overlayContent}
        trigger="hover"
        placement="bottom"
        overlayInnerStyle={{
          background: "#1e293b",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div
          className={`cursor-pointer w-fit px-3 py-1 flex justify-center items-center gap-3 rounded-full border 
  ${
    isWarning
      ? "border-red-500 text-red-500"
      : "border-[#185294] text-[#32D583]"
  } bg-[rgba(239,248,255,.1)]`}
        >
          <div className="flex flex-wrap items-center justify-center gap-[.45rem] text-sm font-medium">
            <span
              className={`w-2 h-2 rounded-full animate-pulse ${
                isWarning ? "bg-red-500" : "bg-[#32D583]"
              }`}
            ></span>

            <span>Monitoring:</span>
            <span>{monitorStatus.display_count} Display(s),</span>
            <span>{monitorStatus.app_count} App(s)</span>
            <span>{isWarning && "(Violations detected)"}</span>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default MonitorStatusBadge;
